import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { clinicsApi, usersApi, adminStatsApi } from '../../lib/api';
import { LiveActivityFeed } from '../../components/superadmin/LiveActivityFeed';
import { CreditCampaignSummary } from '../../components/superadmin/CreditCampaignSummary';
import {
  Building2,
  Users,
  Stethoscope,
  Activity,
  BarChart3,
  PieChart,
  Trophy,
  Heart,
  UserPlus,
  Camera,
  TrendingUp,
  TrendingDown,
  Megaphone,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';

const ROLE_COLORS = {
  doctor: '#5A67D8',
  clinic_admin: '#48BB78',
  employee: '#ED6436',
};

const ROLE_LABELS = {
  doctor: 'Doktor',
  clinic_admin: 'Klinik Yöneticisi',
  employee: 'Çalışan',
};

export const SuperAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalClinics: 0,
    totalUsers: 0,
    totalDoctors: 0,
    activeClinics: 0,
  });
  const [ops, setOps] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [roleDistribution, setRoleDistribution] = useState([]);
  const [doctorsRanking, setDoctorsRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opsLoading, setOpsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [rankingLoading, setRankingLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [clinicsData, usersData, doctorsData] = await Promise.all([
          clinicsApi.list(),
          usersApi.list(),
          usersApi.list({ role: 'doctor' }),
        ]);

        setStats({
          totalClinics: clinicsData.total,
          totalUsers: usersData.total,
          totalDoctors: doctorsData.total,
          activeClinics: clinicsData.clinics.filter((c) => c.is_active).length,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchOps = async () => {
      try {
        const data = await adminStatsApi.getOperational();
        setOps(data);
      } catch (error) {
        console.error('Failed to fetch operational stats:', error);
        setOps(null);
      } finally {
        setOpsLoading(false);
      }
    };
    fetchOps();

    const fetchCharts = async () => {
      try {
        // Monthly growth from real backend endpoint
        const monthlyPromise = adminStatsApi.getMonthly().catch(() => ({ data: [] }));

        // Role distribution derived from real user counts per role
        const roleCountPromises = ['doctor', 'clinic_admin', 'employee'].map((role) =>
          usersApi.list({ role, limit: 1 }).then((r) => ({ role, count: r.total || 0 })).catch(() => ({ role, count: 0 }))
        );

        const [monthlyResult, ...roleCounts] = await Promise.all([
          monthlyPromise,
          ...roleCountPromises,
        ]);

        setMonthlyData(Array.isArray(monthlyResult?.data) ? monthlyResult.data : []);

        const totalRoles = roleCounts.reduce((sum, r) => sum + r.count, 0);
        const distribution = roleCounts.map((r) => ({
          role: r.role,
          name: ROLE_LABELS[r.role],
          color: ROLE_COLORS[r.role],
          count: r.count,
          percent: totalRoles > 0 ? Math.round((r.count / totalRoles) * 100) : 0,
        }));
        setRoleDistribution(distribution);
      } catch (error) {
        console.error('Failed to fetch chart data:', error);
      } finally {
        setChartsLoading(false);
      }
    };

    fetchStats();
    fetchCharts();

    const fetchRanking = async () => {
      try {
        const res = await adminStatsApi.getDoctorsRanking();
        setDoctorsRanking(Array.isArray(res?.data) ? res.data : []);
      } catch (error) {
        console.error('Failed to fetch doctors ranking:', error);
      } finally {
        setRankingLoading(false);
      }
    };
    fetchRanking();
  }, []);

  const today = new Date().toLocaleDateString('tr-TR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const monthlyHasData = monthlyData.some((m) => (m.clinics || 0) + (m.users || 0) > 0);
  const roleHasData = roleDistribution.some((r) => r.count > 0);

  return (
    <DashboardLayout title="Sistem Paneli" subtitle={today}>
      <div className="space-y-6" data-testid="superadmin-dashboard">
        {/* ─── Operational KPI Grid (10 cards) ─────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
          <KpiCard
            label="Toplam Klinik"
            value={opsLoading ? null : (ops?.total_clinics ?? null)}
            icon={Building2}
            tone="primary"
            testid="kpi-total-clinics"
          />
          <KpiCard
            label="Aktif Doktor"
            value={opsLoading ? null : (ops?.active_doctors ?? null)}
            icon={Stethoscope}
            tone="green"
            testid="kpi-active-doctors"
          />
          <KpiCard
            label="Toplam Anne"
            value={opsLoading ? null : (ops?.total_mothers ?? null)}
            icon={Heart}
            tone="pink"
            testid="kpi-total-mothers"
          />
          <KpiCard
            label="Bugün Yeni Hasta"
            value={opsLoading ? null : (ops?.new_patients_today ?? null)}
            icon={UserPlus}
            tone="indigo"
            testid="kpi-new-patients-today"
          />
          <KpiCard
            label="Bugün 4K Üretim"
            value={opsLoading ? null : (ops?.fourk_generated_today ?? null)}
            icon={Camera}
            tone="amber"
            testid="kpi-fourk-today"
          />
          <KpiCard
            label="Bugün Kredi Verildi"
            value={opsLoading ? null : (ops?.credits_granted_today ?? null)}
            icon={TrendingUp}
            tone="emerald"
            testid="kpi-credits-granted"
          />
          <KpiCard
            label="Bugün Kredi Kullanıldı"
            value={opsLoading ? null : (ops?.credits_spent_today ?? null)}
            icon={TrendingDown}
            tone="rose"
            testid="kpi-credits-spent"
          />
          <KpiCard
            label="Aktif Kampanya"
            value={opsLoading ? null : (ops?.active_campaigns ?? null)}
            icon={Megaphone}
            tone="violet"
            testid="kpi-active-campaigns"
          />
          <KpiCard
            label="Başarısız İşlem"
            value={opsLoading ? null : (ops?.failed_scans ?? null)}
            icon={AlertTriangle}
            tone="red"
            testid="kpi-failed-scans"
          />
          <KpiCard
            label="Bekleyen İşlem"
            value={opsLoading ? null : (ops?.pending_scans ?? null)}
            icon={Clock}
            tone="slate"
            testid="kpi-pending-scans"
          />
        </div>

        {/* Live Activity Feed — what's happening right now */}
        <LiveActivityFeed />

        {/* Credit & Campaign summary block */}
        <CreditCampaignSummary />

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Monthly Growth Chart */}
          <Card className="lg:col-span-2 border-0 shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#5A67D8]" />
                    Aylık Büyüme
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">Son 6 ay - Klinik ve kullanıcı artışı</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]" data-testid="monthly-growth-chart">
                {chartsLoading ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    Yükleniyor...
                  </div>
                ) : !monthlyHasData ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm" data-testid="monthly-chart-empty">
                    Henüz yeterli veri yok
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                      <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="clinics" name="Klinikler" fill="#5A67D8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="users" name="Kullanıcılar" fill="#E0D7FF" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Role Distribution Chart */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-[#5A67D8]" />
                    Rol Dağılımı
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">Kullanıcı türlerine göre</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]" data-testid="role-distribution-chart">
                {chartsLoading ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                    Yükleniyor...
                  </div>
                ) : !roleHasData ? (
                  <div className="h-full flex items-center justify-center text-slate-400 text-sm" data-testid="role-chart-empty">
                    Henüz veri yok
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={roleDistribution.filter((r) => r.count > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="count"
                      >
                        {roleDistribution
                          .filter((r) => r.count > 0)
                          .map((entry) => (
                            <Cell key={`cell-${entry.role}`} fill={entry.color} />
                          ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                )}
              </div>
              {!chartsLoading && roleHasData && (
                <div className="space-y-2 mt-4">
                  {roleDistribution.map((item) => (
                    <div key={item.role} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-sm text-slate-600">{item.name}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        {item.count} ({item.percent}%)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Doctor Ranking */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Doktor Sıralaması
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">4K üretim sayısına göre</p>
          </CardHeader>
          <CardContent className="p-0">
            {rankingLoading ? (
              <div className="p-8 text-center text-slate-400 text-sm">Yükleniyor...</div>
            ) : doctorsRanking.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="doctors-ranking-empty">
                Henüz doktor yok
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="doctors-ranking-table">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-medium px-6 py-3 w-16">#</th>
                      <th className="text-left font-medium px-6 py-3">Doktor</th>
                      <th className="text-left font-medium px-6 py-3">Klinik</th>
                      <th className="text-right font-medium px-6 py-3">4K Üretim</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {doctorsRanking.map((d, idx) => {
                      const rank = idx + 1;
                      const rankBadge =
                        rank === 1 ? 'bg-amber-100 text-amber-700' :
                        rank === 2 ? 'bg-slate-200 text-slate-700' :
                        rank === 3 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-50 text-slate-500';
                      return (
                        <tr
                          key={d.doctor_id}
                          className="hover:bg-slate-50 transition-colors"
                          data-testid={`doctor-rank-${rank}`}
                        >
                          <td className="px-6 py-3">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${rankBadge}`}>
                              {rank}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <Link
                              to={`/superadmin/doctors/${d.doctor_id}`}
                              className="font-medium text-slate-800 hover:text-[#5A67D8] transition-colors"
                              data-testid={`doctor-rank-name-${rank}`}
                            >
                              {d.name}
                            </Link>
                          </td>
                          <td className="px-6 py-3 text-slate-600">
                            {d.clinic_name || <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-6 py-3 text-right font-semibold text-[#5A67D8]" data-testid={`doctor-rank-scans-${rank}`}>
                            {d.total_scans}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">Hızlı İşlemler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/superadmin/clinics"
                className="p-4 bg-[#F8F7FC] hover:bg-[#E0D7FF] rounded-xl transition-colors text-left group"
                data-testid="quick-action-add-clinic"
              >
                <Building2 className="w-8 h-8 text-[#5A67D8] mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">Klinik Yönetimi</p>
                <p className="text-sm text-slate-500">Yeni klinik ekle veya mevcut klinikleri yönet</p>
              </Link>
              <Link
                to="/superadmin/users"
                className="p-4 bg-[#F8F7FC] hover:bg-[#E0D7FF] rounded-xl transition-colors text-left group"
                data-testid="quick-action-add-user"
              >
                <Users className="w-8 h-8 text-[#5A67D8] mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">Yeni Kullanıcı Ekle</p>
                <p className="text-sm text-slate-500">Süper admin, klinik yöneticisi, kredi yöneticisi veya çalışan</p>
              </Link>
              <Link
                to="/superadmin/credits"
                className="p-4 bg-[#F8F7FC] hover:bg-[#E0D7FF] rounded-xl transition-colors text-left group"
                data-testid="quick-action-reports"
              >
                <BarChart3 className="w-8 h-8 text-[#5A67D8] mb-2 group-hover:scale-110 transition-transform" />
                <p className="font-medium text-slate-800">Kredi Yönetimi</p>
                <p className="text-sm text-slate-500">Bakiyeler, talepler, paketler ve raporlar</p>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

// ─── Reusable Operational KPI Card ────────────────────────────────
const TONE_STYLES = {
  primary: 'bg-[#5A67D8] text-white',
  green: 'bg-white text-slate-800',
  pink: 'bg-white text-slate-800',
  indigo: 'bg-white text-slate-800',
  amber: 'bg-white text-slate-800',
  emerald: 'bg-white text-slate-800',
  rose: 'bg-white text-slate-800',
  violet: 'bg-white text-slate-800',
  red: 'bg-white text-slate-800',
  slate: 'bg-white text-slate-800',
};
const ICON_BG = {
  primary: 'bg-white/20 text-white',
  green: 'bg-green-100 text-green-600',
  pink: 'bg-pink-100 text-pink-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  amber: 'bg-amber-100 text-amber-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  rose: 'bg-rose-100 text-rose-600',
  violet: 'bg-violet-100 text-violet-600',
  red: 'bg-red-100 text-red-600',
  slate: 'bg-slate-100 text-slate-600',
};

const KpiCard = ({ label, value, icon: Icon, tone = 'slate', testid }) => {
  const isPrimary = tone === 'primary';
  const labelClass = isPrimary ? 'text-white/85' : 'text-slate-500';
  const display = value === null || value === undefined ? '—' : (typeof value === 'number' ? value.toLocaleString('tr-TR') : value);
  const isEmpty = value === null || value === undefined;
  return (
    <Card className={`${TONE_STYLES[tone]} border-0 shadow-md hover:shadow-lg transition-shadow`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className={`text-xs font-medium ${labelClass} truncate`}>{label}</p>
            <p className={`text-2xl font-bold mt-1 ${isPrimary ? '' : 'text-slate-800'}`} data-testid={testid}>
              {isEmpty ? <span className="text-sm text-slate-400">Veri yok</span> : display}
            </p>
          </div>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${ICON_BG[tone]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperAdminDashboard;
